import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import {
  ComputerTool,
  FileSearchTool,
  FunctionTool,
  ModelResponse,
  ReasoningConfiguration,
  ResponseInputItem,
  ResponseModel,
  ResponseTextConfig,
  ToolChoice,
  WebSearchTool,
} from 'types/open_ai';

import { useAPI } from './api/useAPI';

type UseCreateModelResponseParams = {
  input: string | Array<ResponseInputItem>;
  model: ResponseModel;
  include?: Array<
    | 'file_search_call.results'
    | 'message.input_image.image_url'
    | 'computer_call_output.output.image_url'
  > | null;
  instructions?: string | null;
  metadata?: Record<string, string> | null;
  /** @default true */
  parallel_tool_calls?: boolean | null;
  previous_response_id?: string | null;
  reasoning?: ReasoningConfiguration | null;
  /** @default true */
  store?: boolean | null;
  stream?: boolean | null;
  temperature?: number | null;
  text?: ResponseTextConfig;
  tool_choice?: ToolChoice;
  tools?: Array<FileSearchTool | FunctionTool | ComputerTool | WebSearchTool>;
  top_p?: number | null;
  truncation?: 'auto' | 'disabled' | null;
};

/** Create a OpenAI model response */
export function useCreateModelResponse({
  ...options
}: UseMutationOptions<
  ModelResponse,
  unknown,
  UseCreateModelResponseParams
> = {}) {
  const { applicationID, client } = useAPI();

  return useMutation({
    ...options,
    mutationFn: async (params: UseCreateModelResponseParams) => {
      const { data } = await client.post(
        `/apps/${applicationID}/assistant/code/responses`,
        params,
        {
          baseURL: `${import.meta.env.VITE_API_URL}/integrations`,
        }
      );

      return data;
    },
  });
}
